import {
  Box, Button, Container, IconButton, Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { motion } from 'motion/react'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useSnackbar } from 'notistack'
import UseTranslation from '../../../hooks/UseTranslation'
import CardWithSpace from '../../../components/Cards/CardWithSpace'
import HoverTooltip from '../../../components/PageHelpers/HoverTooltip'
import { decodeHtmlString, getFormattedDateWithDay, addToArrIfNotExistOrRemove } from '../../../utils/helpers'
import TitleWithIcon from '../../../components/Cards/helpers/TitleWithIcon'
import SeeMoreText from '../../../components/Cards/helpers/SeeMoreText'
import { ReactComponent as ReceivingDate } from '../../../images/ReceivingDate.svg'
import { ReactComponent as RequestType } from '../../../images/RequestType.svg'
import { ReactComponent as Manager } from '../../../images/Manager.svg'
import { ReactComponent as Approve } from '../../../images/Approve.svg'
import { ReactComponent as Reject } from '../../../images/Reject.svg'
import { selectNotificationInbox } from '../../../redux/features/notifications/notificationInbox/notificationInboxSlice'
import NoDataFound from '../../../components/PageHelpers/NoDataFound'
import ActionNotificationFormModal from '../helper/ActionNotificationFormModal'
import { cardVariants } from '../../../utils/animation'
import { notificationInboxResetAlertHandler, retrieveNotificationInboxWithQueries }
  from '../../../redux/features/notifications/notificationInbox/notificationInboxThunk'
import OfoqCheckbox from '../../../components/Forms/OfoqCheckBox'
import DetailsPopUp from '../helper/NotificationDetailModel'
import LoaderWithErrorHandler from '../../../components/PageHelpers/LoaderWithErrorHandler'
import InboxCardListSkeleton from './InboxCardListSkeleton'
import LoadMoreBtn from '../../../components/PageHelpers/LoadMoreBtn'

const InboxCardList = ({
  selectionMode, selectedCards, setSelectedCards,
}) => {
  const notificationInboxStore = useSelector(selectNotificationInbox)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { t, language } = UseTranslation()
  const dispatch = useDispatch()
  const [notificationModalStatus, setNotificationModalStatus] = useState({
    status: false,
    type: 'approve',
  })
  const [notificationModelData, setNotificationModelData] = useState()
  const [notificationState, setNotificationState] = useState({
    status: false,
    data: null,
  })

  const handleCardClick = (notification) => {
    if (selectionMode) {
      if (selectedCards.length >= 10 && !selectedCards.some((obj) => obj.id === notification.id)) {
        enqueueSnackbar(
          <Box onClick={() => closeSnackbar()} style={{ cursor: 'pointer' }}>
            {t?.['You have reached the maximum limit of selecting requests ( 10 Requests )']}
          </Box>,
          {
            variant: 'error',
          },
        )
        return;
      }
      const newAr = addToArrIfNotExistOrRemove(selectedCards, 'id', notification)
      setSelectedCards(newAr)
    } else {
      setNotificationState((prevState) => ({
        status: !prevState.status,
        data: notification,
      }))
    }
  }

  const handelNotificationModel = (type, data, event) => {
    event.stopPropagation()

    setNotificationModalStatus({
      status: true,
      type,
    })
    setNotificationModelData(data)
  }

  return (
    <Container maxWidth={false} sx={styles.container} data-testid="inboxListContainer">
      {notificationInboxStore?.data?.map((notification, index) => {
        const isItemSelected = selectedCards?.some((obj) => obj?.id === notification?.id)
        return (
          <Box
            key={notification?.id}
            component={motion.div}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={index}
            sx={[styles.card, selectionMode ? styles.disableHover : styles.cardWithHover,
              isItemSelected && styles.selectedCard,
            ]}
            onClick={() => handleCardClick(notification)}
            data-testid="Inbox-item"
          >
            <CardWithSpace customStyles={styles.cardContent} isClickable={!selectionMode}>
              {selectionMode && (
              <OfoqCheckbox
                name=""
                defaultOption={isItemSelected}
                refState={() => {}}
                customStyle={styles.checkbox}
              />
              )}
              <Box sx={styles.cardDataContainer}>
                <Box sx={styles.cardsContainer}>
                  <Box sx={styles.cardHeader}>
                    <TitleWithIcon
                      title={(
                        <Box sx={styles.cardTitle}>
                          <Typography variant="heading3">
                            <Box component="span" sx={styles.cardTitleCode}>
                              {`#${notification?.employee?.code}`}
                            </Box>
                            {` - ${notification?.employee?.name}`}
                          </Typography>

                        </Box>
                  )}
                      icon={<Manager />}
                      customStyle={styles.stackFlex}
                    />
                    <Box sx={[selectionMode
                      ? styles.disableActionBtn : styles.responsiveActionBtn]}>
                      { language === 'EN' ? <KeyboardArrowRightIcon className="arrowIcon" />
                        : <KeyboardArrowLeftIcon className="arrowIcon" />}
                    </Box>
                  </Box>
                  <SeeMoreText
                  text={decodeHtmlString(notification?.description)}
                  customStyle={styles.mobileNotesInfo}
                  />
                  <Box sx={styles.cardInfoContainer}>
                    <Box sx={styles.rowFlex}>
                      <TitleWithIcon
                        title={(
                          <Box sx={styles.requestedOnContainer}>
                            <Typography variant="body1" sx={styles.requestedOnDate}>
                              {`${notification?.source_type?.value}`}
                            </Typography>
                          </Box>
                    )}
                        icon={(
                          <HoverTooltip title={t?.['Request Type']}>
                            <RequestType />
                          </HoverTooltip>
                        )}
                        iconSize="sm"
                        textVariant="heading8"
                        customStyle={styles.iconWithText}
                      />
                    </Box>
                    <Box sx={styles.rowFlex}>
                      <TitleWithIcon
                        title={(
                          <Box sx={styles.requestedOnContainer}>
                            <Typography variant="body1" sx={styles.requestedOnDate}>
                              {getFormattedDateWithDay(notification?.notify_time)}
                            </Typography>
                          </Box>
                    )}
                        icon={(
                          <HoverTooltip title={t?.['Receiving Date']}>
                            <ReceivingDate />
                          </HoverTooltip>
                        )}
                        iconSize="sm"
                        textVariant="heading8"
                        customStyle={styles.iconWithText}
                      />
                    </Box>
                  </Box>
                </Box>
                {!selectionMode && (
                <Box sx={styles.cardAction}>
                  <IconButton
                    onClick={(event) => handelNotificationModel('approve', notification, event)}
                    sx={styles.approveButton}
                  >
                    <HoverTooltip title={t?.Approve}>
                      <Approve />
                    </HoverTooltip>
                    <Typography variant="body1">
                      {t?.Approve}
                    </Typography>
                  </IconButton>
                  <IconButton
                    onClick={(event) => handelNotificationModel('reject', notification, event)}
                    sx={styles.rejectButton}
                  >
                    <HoverTooltip title={t?.Reject}>
                      <Reject />
                    </HoverTooltip>
                    <Typography variant="body1">
                      {t?.Reject}
                    </Typography>
                  </IconButton>
                  <Box
                    component={Button}
                    sx={[selectionMode ? styles.disableActionBtn : styles.ActionBtn]}
                  >
                    { language === 'EN' ? <KeyboardArrowRightIcon className="arrowIcon" />
                      : <KeyboardArrowLeftIcon className="arrowIcon" />}
                  </Box>
                </Box>
                )}
              </Box>
            </CardWithSpace>
          </Box>
        )
      })}
      <DetailsPopUp
        modalStatus={notificationState}
        setModalStatus={setNotificationState}
        actionAfterSuccess={
          () => dispatch(retrieveNotificationInboxWithQueries({ resetQueries: true }))
        }
        showActions
      />
      <NoDataFound store={notificationInboxStore} customStyle={styles.noDataFound} />
      <ActionNotificationFormModal
        modalStatus={notificationModalStatus}
        setModalStatus={setNotificationModalStatus}
        data={notificationModelData}
        actionAfterSuccess={
          () => dispatch(retrieveNotificationInboxWithQueries({ resetQueries: true }))
        }
      />
      <LoaderWithErrorHandler
          store={notificationInboxStore}
          resetAlertHandler={notificationInboxResetAlertHandler}
          Skeleton={InboxCardListSkeleton}
        />
      <LoadMoreBtn
        store={notificationInboxStore}
        actionToDispatch={retrieveNotificationInboxWithQueries}
        sendQueryAsObject />

    </Container>
  )
}

export const styles = {
  container: {
    paddingInline: { xs: '0px', sm: '0px' },
    paddingBottom: { xs: '120px', sm: '0px' },
  },
  card: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '12px',
    marginTop: { xs: '5px', sm: '16px' },
    height: { sm: '150px' },
    cursor: 'pointer',
  },
  noDataFound: {
    '& img': {
      height: { xs: '220px', sm: '365px' },
    },
  },
  cardContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0px !important',
    padding: { xs: '0 10px', sm: '0px 20px' },
  },
  checkbox: {
    marginRight: { xs: '0px !important', sm: '15px !important' },
  },
  cardDataContainer: {
    display: 'flex',
    width: '100%',
    overflow: { xs: 'none', sm: 'hidden' },
    flexDirection: { xs: 'column', sm: 'row' },
  },
  cardsContainer: {
    padding: { xs: '7px', sm: '15px 0px' },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: { xs: 'space-around', sm: 'flex-start' },
    width: { xs: '100%', sm: '88%' },
    gap: { xs: '0px', sm: '8px' },
  },
  cardHeader: {
    marginBottom: { xs: '5px', md: '0px' },
    width: { xs: '95%', sm: '120%' },
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: { xs: '0px 0px 0px 20px', md: '0px 10px 0px 0px', lg: '0px' },
    gap: { xs: '0px', sm: '14px' },
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: { xs: '12px', md: '0px' },
  },
  cardTitleCode: {
    color: { xs: 'primary.contrastText', sm: 'grey.500' },
    fontSize: { xs: '12px', sm: '18px' },

  },
  stackFlex: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      display: { xs: 'none', md: 'flex' },
      height: { xs: '15px', sm: '20px' },
      width: { xs: '15px', sm: '20px' },
      filter: 'brightness(0) saturate(100%) invert(60%) sepia(98%) saturate(331%) hue-rotate(90deg) brightness(91%) contrast(91%)',
    },
    '& span': {
      marginLeft: { xs: '-13px', md: '5px' },
    },
  },
  mobileNotesInfo: {
    alignItems: 'center',
    paddingLeft: { xs: '0px', md: '20px' },
    margin: { xs: '5px 0px', md: '5px 10px 0px' },
    color: 'grey.500',
    '& span': {
      overflowWrap: 'break-word',
    },
    '& p': {
      margin: { xs: '0px', md: '5px' },
      fontSize: { xs: '12px', sm: '14px' },
    },
  },
  cardInfoContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
  },
  rowFlex: {
    maxWidth: { xs: 'unset', sm: '350px' },
    width: { xs: 'unset', sm: '100%' },
    display: 'flex',
    justifyContent: 'space-between',
    padding: { xs: '3px 0px 0px', md: '0px 15px 0px 0px' },
  },
  iconWithText: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '10px',
    marginRight: '0px',
    alignItems: 'flex-start',
    '& svg': {
      filter: 'brightness(0) saturate(100%) invert(60%) sepia(98%) saturate(331%) hue-rotate(90deg) brightness(91%) contrast(91%)',
    },
    '& p': {
      margin: { xs: '0px', md: '0px 0px 0px 5px' },
    },
  },
  requestedOnContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& p': {
      fontSize: { xs: '12px', sm: '14px' },
    },
  },
  requestedOnDate: {
    marginLeft: '5px',
    direction: 'ltr /* @noflip */',
  },
  cardAction: {
    display: 'flex',
    justifyContent: { xs: 'center', sm: 'flex-end' },
    alignItems: 'center',
    margin: { xs: '5px 0px', sm: '0px' },
    gap: { xs: '98px', sm: '0px' },
    marginRight: { xs: '0px', md: '-30px' },
    borderTop: (theme) => ({
      xs: `1px solid ${theme.palette.grey[200]}`,
      sm: 'none',
    }),
    '& p': {
      display: { xs: 'block', sm: 'none' },
      fontSize: { xs: '13px', sm: '14px' },
      marginLeft: { xs: '5px', sm: '0px' },
      fontWeight: 800,
    },
    '& svg': {
      width: { xs: 20, sm: 40 },
      height: { xs: 20, sm: 40 },
    },
  },
  approveButton: {
    color: 'primary.main',
    backgroundColor: 'background.paper',
    '&:hover': {
      backgroundColor: 'background.paper',
    },
    margin: '0 5px',
  },
  rejectButton: {
    color: 'error.main',
    backgroundColor: 'background.paper',
    '&:hover': {
      backgroundColor: 'background.paper',
    },
    margin: '0 5px',
  },
  disableActionBtn: {
    display: 'none',
  },
  actionBtn: {
    background: 'transparent',
    boxShadow: 'none',
    '& .arrowIcon': {
      display: 'none',
    },
    '&:hover': {
      background: 'transparent',
      boxShadow: 'none',
    },
    '& svg': {
      height: { xs: '20px', sm: '25px !important' },
      width: { xs: '20px', sm: '25px !important' },
    },
  },
  cardWithHover: {
    '&:hover .arrowIcon': {
      display: 'flex',
      color: 'primary.main',
      background: 'transparent',
      boxShadow: 'none',
    },
  },
  disableHover: {
    '&:hover .arrowIcon': {
      display: 'none',
      cursor: 'not-allowed',
    },
  },
  responsiveActionBtn: {
    background: 'transparent',
    boxShadow: 'none',
    display: { xs: 'flex', md: 'none' },
    '& .arrowIcon': {
      display: 'flex',
    },
    '&:hover': {
      background: 'transparent',
      boxShadow: 'none',
    },
    '& svg': {
      filter: 'brightness(0) saturate(10%) invert(0) contrast(10%)',
      height: '20px',
      width: '20px',
    },
  },
  ActionBtn: {
    background: 'transparent',
    boxShadow: 'none',
    display: { xs: 'none', md: 'flex' },
    '& .arrowIcon': {
      display: 'none',
    },
    '&:hover': {
      background: 'transparent',
      boxShadow: 'none',
    },
    '& svg': {
      height: { xs: '20px', sm: '25px !important' },
      width: { xs: '20px', sm: '25px !important' },
    },
  },
  selectedCard: {
    border: (theme) => `2px solid ${theme.palette.primary.main}`,
  },
}

export default InboxCardList
